import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { apiEndpoints } from '../utils/api';
import { useUserActivityStore } from './userActivityStore';

interface SandboxStatus {
  status: 'idle' | 'loading' | 'creating' | 'resuming' | 'ready' | 'sleeping' | 'error' | 'not_applicable';
  sandboxId: string | null;
  taskId: string | null;
  message: string | null;
  lastUpdated: number;
}

interface PollingState {
  intervalId: NodeJS.Timeout | null;
  subscriberCount: number;
  isPolling: boolean;
  mode: 'active' | 'idle';
  sleepRequested: boolean;
  api?: AuthenticatedApi;
  intervalMs: number;
}

interface AuthenticatedApi {
  get: <T = any>(endpoint: string, options?: RequestInit) => Promise<T>;
  post: <T = any>(endpoint: string, data?: any, options?: RequestInit) => Promise<T>;
  put: <T = any>(endpoint: string, data?: any, options?: RequestInit) => Promise<T>;
  delete: <T = any>(endpoint: string, options?: RequestInit) => Promise<T>;
  patch: <T = any>(endpoint: string, data?: any, options?: RequestInit) => Promise<T>;
}

interface SandboxStore {
  // State
  sandboxStatuses: Record<string, SandboxStatus>;
  pollingStates: Record<string, PollingState>;
  agentActivity: Record<string, boolean>;
  userIdle: boolean;
  
  // Actions
  setSandboxStatus: (workspaceId: string, status: Partial<SandboxStatus>) => void;
  resetSandbox: (workspaceId: string) => void;
  clearSandboxStatus: (workspaceId: string) => void;
  
  // Polling Management
  startPolling: (workspaceId: string, api: AuthenticatedApi) => void;
  stopPolling: (workspaceId: string) => void;
  subscribeToWorkspace: (workspaceId: string, api: AuthenticatedApi) => () => void;
  setUserIdleState: (isIdle: boolean) => void;
  setWorkspaceAgentActivity: (workspaceId: string, isActive: boolean) => void;
  
  // Debug
  debugInfo: () => {
    activeWorkspaces: number;
    pollingWorkspaces: number;
    totalSubscribers: number;
  };
  
  // Internal
  _pollSandboxStatus: (workspaceId: string, api: AuthenticatedApi, mode: 'active' | 'idle') => Promise<void>;
  _requestSandboxSleep: (workspaceId: string, api: AuthenticatedApi) => Promise<void>;
  _switchToIdleMode: (workspaceId: string, api: AuthenticatedApi) => Promise<void>;
  _switchToActiveMode: (workspaceId: string, api: AuthenticatedApi, immediate?: boolean) => Promise<void>;
}

const defaultStatus: SandboxStatus = {
  status: 'idle',
  sandboxId: null,
  taskId: null,
  message: null,
  lastUpdated: Date.now(),
};

const ACTIVE_POLL_INTERVAL_MS = 2_000;
const READY_POLL_INTERVAL_MS = 10_000;
const IDLE_STATUS_INTERVAL_MS = 60_000;

export const useSandboxStore = create<SandboxStore>()(
  devtools(
    subscribeWithSelector((set, get) => {
      const scheduleInterval = (
        workspaceId: string,
        api: AuthenticatedApi,
        intervalMs: number,
        mode: 'active' | 'idle',
      ) =>
        setInterval(() => {
          void get()._pollSandboxStatus(workspaceId, api, mode);
        }, intervalMs);

      return {
        sandboxStatuses: {},
        pollingStates: {},
        agentActivity: {},
        userIdle: false,

        setSandboxStatus: (workspaceId, statusUpdate) => {
          set((state) => ({
            sandboxStatuses: {
              ...state.sandboxStatuses,
              [workspaceId]: {
                ...defaultStatus,
                ...state.sandboxStatuses[workspaceId],
                ...statusUpdate,
                lastUpdated: Date.now(),
              },
            },
          }));
        },

        resetSandbox: (workspaceId) => {
          set((state) => ({
            sandboxStatuses: {
              ...state.sandboxStatuses,
              [workspaceId]: {
                ...defaultStatus,
                status: 'loading',
                message: 'Resetting sandbox...',
                lastUpdated: Date.now(),
              },
            },
          }));
        },

        clearSandboxStatus: (workspaceId) => {
          const { stopPolling } = get();
          stopPolling(workspaceId);

          set((state) => {
            const { [workspaceId]: _, ...restStatuses } = state.sandboxStatuses;
            const { [workspaceId]: __, ...restPolling } = state.pollingStates;
            const { [workspaceId]: ___, ...restAgentActivity } = state.agentActivity;
            return {
              sandboxStatuses: restStatuses,
              pollingStates: restPolling,
              agentActivity: restAgentActivity,
            };
          });
        },

        startPolling: (workspaceId, api) => {
          const pollingState = get().pollingStates[workspaceId];
          if (pollingState?.isPolling) {
            if (process.env.NODE_ENV === 'development') {
              console.log(`[SandboxStore] Already polling for workspace ${workspaceId}`);
            }
            return;
          }

          if (process.env.NODE_ENV === 'development') {
            console.log(`[SandboxStore] Starting polling for workspace ${workspaceId}`);
          }

          set((state) => ({
            pollingStates: {
              ...state.pollingStates,
              [workspaceId]: {
                intervalId: null,
                subscriberCount: state.pollingStates[workspaceId]?.subscriberCount || 0,
                isPolling: true,
                mode: 'active',
                sleepRequested: false,
                api,
                intervalMs: ACTIVE_POLL_INTERVAL_MS,
              },
            },
          }));

          void get()._switchToActiveMode(workspaceId, api, true);
        },

        stopPolling: (workspaceId) => {
          const pollingState = get().pollingStates[workspaceId];
          if (!pollingState) {
            return;
          }

          if (pollingState.intervalId) {
            clearInterval(pollingState.intervalId);
          }

          set((state) => ({
            pollingStates: {
              ...state.pollingStates,
              [workspaceId]: {
                intervalId: null,
                subscriberCount: 0,
                isPolling: false,
                mode: 'active',
                sleepRequested: false,
                api: pollingState.api,
                intervalMs: ACTIVE_POLL_INTERVAL_MS,
              },
            },
          }));
        },

        subscribeToWorkspace: (workspaceId, api) => {
          const { startPolling, stopPolling } = get();

          set((state) => ({
            pollingStates: {
              ...state.pollingStates,
              [workspaceId]: {
                intervalId: state.pollingStates[workspaceId]?.intervalId || null,
                isPolling: state.pollingStates[workspaceId]?.isPolling || false,
                subscriberCount: (state.pollingStates[workspaceId]?.subscriberCount || 0) + 1,
                mode: state.pollingStates[workspaceId]?.mode || 'active',
                sleepRequested: state.pollingStates[workspaceId]?.sleepRequested || false,
                api,
                intervalMs: state.pollingStates[workspaceId]?.intervalMs || ACTIVE_POLL_INTERVAL_MS,
              },
            },
          }));

          if ((get().pollingStates[workspaceId]?.subscriberCount ?? 0) === 1) {
            startPolling(workspaceId, api);
          }

          return () => {
            const currentState = get().pollingStates[workspaceId];
            const newCount = Math.max(0, (currentState?.subscriberCount || 1) - 1);

            set((state) => ({
              pollingStates: {
                ...state.pollingStates,
                [workspaceId]: {
                  ...state.pollingStates[workspaceId],
                  subscriberCount: newCount,
                },
              },
            }));

            if (newCount === 0) {
              stopPolling(workspaceId);
            }
          };
        },

        setUserIdleState: (isIdle) => {
          const currentIdleState = get().userIdle;
          if (currentIdleState === isIdle) {
            return;
          }

          set({ userIdle: isIdle });

          const entries = Object.entries(get().pollingStates);
          entries.forEach(([workspaceId, pollingState]) => {
            const api = pollingState.api;
            if (!api) {
              return;
            }
            if (isIdle) {
              void get()._switchToIdleMode(workspaceId, api);
            } else {
              void get()._switchToActiveMode(workspaceId, api, true);
            }
          });
        },

        setWorkspaceAgentActivity: (workspaceId, isActive) => {
          set((state) => ({
            agentActivity: {
              ...state.agentActivity,
              [workspaceId]: isActive,
            },
          }));
        },

        debugInfo: () => {
          const state = get();
          return {
            activeWorkspaces: Object.keys(state.sandboxStatuses).length,
            pollingWorkspaces: Object.values(state.pollingStates).filter((p) => p.isPolling).length,
            totalSubscribers: Object.values(state.pollingStates).reduce((sum, p) => sum + p.subscriberCount, 0),
          };
        },

        _switchToActiveMode: async (workspaceId, api, immediate = false) => {
          const pollingState = get().pollingStates[workspaceId];
          if (!pollingState) {
            return;
          }

          if (pollingState.intervalId) {
            clearInterval(pollingState.intervalId);
          }

          const intervalId = scheduleInterval(workspaceId, api, ACTIVE_POLL_INTERVAL_MS, 'active');

          set((state) => ({
            pollingStates: {
              ...state.pollingStates,
              [workspaceId]: {
                ...state.pollingStates[workspaceId],
                intervalId,
                mode: 'active',
                sleepRequested: false,
                api,
                intervalMs: ACTIVE_POLL_INTERVAL_MS,
              },
            },
          }));

          if (immediate) {
            await get()._pollSandboxStatus(workspaceId, api, 'active');
          }
        },

        _switchToIdleMode: async (workspaceId, api) => {
          const pollingState = get().pollingStates[workspaceId];
          if (!pollingState) {
            return;
          }

          if (pollingState.mode === 'idle' && pollingState.intervalId) {
            return;
          }

          if (pollingState.intervalId) {
            clearInterval(pollingState.intervalId);
          }

          await get()._requestSandboxSleep(workspaceId, api);

          const intervalId = scheduleInterval(workspaceId, api, IDLE_STATUS_INTERVAL_MS, 'idle');

          set((state) => ({
            pollingStates: {
              ...state.pollingStates,
              [workspaceId]: {
                ...state.pollingStates[workspaceId],
                intervalId,
                mode: 'idle',
                api,
                intervalMs: IDLE_STATUS_INTERVAL_MS,
              },
            },
          }));

          await get()._pollSandboxStatus(workspaceId, api, 'idle');
        },

        _requestSandboxSleep: async (workspaceId, api) => {
          const pollingState = get().pollingStates[workspaceId];
          const currentStatus = get().sandboxStatuses[workspaceId];
          if (!pollingState || pollingState.sleepRequested) {
            return;
          }

          if (!currentStatus || ['not_applicable', 'sleeping'].includes(currentStatus.status)) {
            set((state) => ({
              pollingStates: {
                ...state.pollingStates,
                [workspaceId]: {
                  ...state.pollingStates[workspaceId],
                  sleepRequested: true,
                },
              },
            }));
            return;
          }

          if (currentStatus.status !== 'ready') {
            return;
          }

          if (get().agentActivity[workspaceId]) {
            if (process.env.NODE_ENV === 'development') {
              console.log(`[SandboxStore] Skipping sleep for workspace ${workspaceId} because an agent is active`);
            }
            return;
          }

          try {
            const response = await api.post(apiEndpoints.workspaceSleepSandbox(workspaceId));
            set((state) => ({
              pollingStates: {
                ...state.pollingStates,
                [workspaceId]: {
                  ...state.pollingStates[workspaceId],
                  sleepRequested: true,
                },
              },
            }));

            if (response.state === 'sleeping') {
              get().setSandboxStatus(workspaceId, {
                status: 'sleeping',
                sandboxId: response.persistent_sandbox_id ?? null,
                taskId: response.sandbox_launch_task_id ?? null,
                message: 'Sandbox is sleeping',
              });
            }
          } catch (error) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[SandboxStore] Failed to sleep sandbox:', error);
            }
          }
        },

        _pollSandboxStatus: async (workspaceId, api, mode) => {
          const pollingState = get().pollingStates[workspaceId];
          if (mode === 'idle') {
            try {
              const response = await api.get<{
                state: string;
                active_sandbox_id?: string | null;
                persistent_sandbox_id?: string | null;
                sandbox_launch_task_id?: string | null;
                sandbox_sleep_started_at?: string | null;
                sandbox_last_used?: string | null;
                has_active_jobs?: boolean;
                sleep_reason?: string;
              }>(apiEndpoints.workspaceSandboxStatus(workspaceId));

              const state = response.state;
              set((prevState) => ({
                agentActivity: {
                  ...prevState.agentActivity,
                  [workspaceId]: Boolean(response.has_active_jobs),
                },
              }));
              if (state === 'ready') {
                get().setSandboxStatus(workspaceId, {
                  status: 'ready',
                  sandboxId: response.active_sandbox_id || null,
                  taskId: null,
                  message: 'Sandbox is ready',
                });

                if (pollingState && !pollingState.sleepRequested) {
                  await get()._requestSandboxSleep(workspaceId, api);
                }
              } else if (state === 'sleeping') {
                get().setSandboxStatus(workspaceId, {
                  status: 'sleeping',
                  sandboxId: response.persistent_sandbox_id || null,
                  taskId: response.sandbox_launch_task_id || null,
                  message: response.sleep_reason === 'never_started' ? 'Sandbox is dormant' : 'Sandbox is sleeping',
                });
              } else if (state === 'creating' || state === 'resuming') {
                get().setSandboxStatus(workspaceId, {
                  status: state as 'creating' | 'resuming',
                  sandboxId: null,
                  taskId: response.sandbox_launch_task_id || null,
                  message: state === 'creating' ? 'Sandbox is creating...' : 'Sandbox is resuming...',
                });
              } else if (state === 'error') {
                get().setSandboxStatus(workspaceId, {
                  status: 'error',
                  sandboxId: null,
                  taskId: null,
                  message: 'Sandbox is unavailable',
                });
              } else if (state === 'not_applicable') {
                get().setSandboxStatus(workspaceId, {
                  status: 'not_applicable',
                  sandboxId: null,
                  taskId: null,
                  message: 'This workspace does not use sandboxes',
                });
              }
            } catch (error) {
              if (process.env.NODE_ENV === 'development') {
                console.warn('[SandboxStore] Failed to fetch sandbox status:', error);
              }
            }
            return;
          }

          try {
            const response = await api.post<{
              status: string;
              sandbox_id?: string;
              task_id?: string;
              message?: string;
            }>(apiEndpoints.workspaceEnsureSandbox(workspaceId));

            const updatedPollingState = get().pollingStates[workspaceId];

            if (response.status === 'not_applicable') {
              get().setSandboxStatus(workspaceId, {
                status: 'not_applicable',
                sandboxId: null,
                taskId: null,
                message: response.message || 'This workspace does not use sandboxes',
              });
              get().stopPolling(workspaceId);
            } else if (response.status === 'ready') {
              get().setSandboxStatus(workspaceId, {
                status: 'ready',
                sandboxId: response.sandbox_id || null,
                taskId: null,
                message: 'Sandbox is ready',
              });

              if (updatedPollingState && updatedPollingState.intervalMs !== READY_POLL_INTERVAL_MS) {
                if (updatedPollingState.intervalId) {
                  clearInterval(updatedPollingState.intervalId);
                }
                const intervalId = scheduleInterval(workspaceId, api, READY_POLL_INTERVAL_MS, 'active');
                set((state) => ({
                  pollingStates: {
                    ...state.pollingStates,
                    [workspaceId]: {
                      ...state.pollingStates[workspaceId],
                      intervalId,
                      intervalMs: READY_POLL_INTERVAL_MS,
                    },
                  },
                }));
              }

              if (useUserActivityStore.getState().isIdle && updatedPollingState?.mode === 'idle' && !updatedPollingState.sleepRequested) {
                await get()._requestSandboxSleep(workspaceId, api);
              }
            } else if (response.status === 'creating' || response.status === 'resuming') {
              get().setSandboxStatus(workspaceId, {
                status: response.status as 'creating' | 'resuming',
                sandboxId: null,
                taskId: response.task_id || null,
                message: response.message || `Sandbox is ${response.status}...`,
              });

              if (updatedPollingState && updatedPollingState.intervalMs !== ACTIVE_POLL_INTERVAL_MS) {
                if (updatedPollingState.intervalId) {
                  clearInterval(updatedPollingState.intervalId);
                }
                const intervalId = scheduleInterval(workspaceId, api, ACTIVE_POLL_INTERVAL_MS, 'active');
                set((state) => ({
                  pollingStates: {
                    ...state.pollingStates,
                    [workspaceId]: {
                      ...state.pollingStates[workspaceId],
                      intervalId,
                      intervalMs: ACTIVE_POLL_INTERVAL_MS,
                    },
                  },
                }));
              }
            }
          } catch (error) {
            if (process.env.NODE_ENV === 'development') {
              console.error('[SandboxStore] Failed to poll sandbox status:', error);
            }
            get().setSandboxStatus(workspaceId, {
              status: 'error',
              sandboxId: null,
              taskId: null,
              message: error instanceof Error ? error.message : 'Failed to check sandbox status',
            });
          }
        },
      };
    }),
    {
      name: 'sandbox-store',
    },
  ),
);

// Export a hook to get debug info easily
export const useSandboxDebugInfo = () => {
  return useSandboxStore((state) => state.debugInfo);
};

// Development helper - expose store to window for debugging
if (process.env.NODE_ENV === 'development') {
  (window as any).sandboxStore = useSandboxStore;
  (window as any).getSandboxDebugInfo = () => useSandboxStore.getState().debugInfo();
} 